import React from "react";
import { cx } from "../../utils/classNames";

export type ProseSize = "md" | "sm" | "lede";
export type ProseTone = "editorial" | "neutral";

/**
 * Prose
 *
 * Editorial composition wrapper. Long-form content — blog posts, customer
 * stories, page-hero descriptions, anything with running paragraphs and
 * headings — wraps in Prose and the descendant `h1`–`h6`, `p`, `ul`, `ol`,
 * `blockquote`, `a`, `code`, and `hr` elements pick up the editorial type
 * system, spacing rules, inline-element styling, and the 42rem reading
 * column.
 *
 * Authors don't put role class names on individual elements — the wrapper
 * owns them all. This is a composition pattern, not a role family: inside
 * a Prose surface, headings use the brand face (Rethink Sans) at fluid
 * `clamp()` sizes; body type bumps to 18px / 1.55 with hyphenation; links
 * use the iris underline treatment; inline `<code>` gets the warm pill.
 *
 * For dense product UI (table cells, form labels, button text), prefer the
 * semantic role utilities directly (`text-heading-*`, `text-body-*`,
 * `text-label-*`). Prose is for editorial moments — where a reader settles
 * in to read.
 *
 * For agent output and other dense rich-text contexts, see the standalone
 * `Markdown` primitive in `@texturehq/edges-agents` — it ships its own
 * type ramp, spacing, and table styling rather than reusing Prose's
 * editorial rules.
 *
 * @see https://edges.texturehq.com/foundations/typography
 *
 * Sizes:
 * - `md` (default) — 18px body / 42rem column. The editorial composition.
 * - `sm` — 16px body / 36rem column. Footnotes, captions, prop ledes.
 * - `lede` — first paragraph rendered at 18px in `--color-text-secondary`.
 *   Use for a single emphasized page-hero lede paragraph.
 *
 * Tones:
 * - `editorial` (default) — iris-colored links with the long underline.
 *   Use on marketing/editorial surfaces.
 * - `neutral` — text-primary links with a subtle gray underline. Use in
 *   product contexts (forms, in-app chrome, banners) where the editorial
 *   iris would compete with the surface's brand action color.
 *
 * @example
 * ```tsx
 * <Prose>
 *   <h1>The grid, orchestrated.</h1>
 *   <p>
 *     Edges is the unified design system that powers Texture's product and
 *     marketing surfaces.
 *   </p>
 * </Prose>
 * ```
 *
 * @example For raw HTML, MDX, or CMS-rendered content:
 * ```tsx
 * <Prose dangerouslySetInnerHTML={{ __html: articleHtml }} />
 * ```
 */
export interface ProseProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The element to render as. Defaults to `div`; use `as="article"` when
   * the Prose surface is itself the article landmark, or `as="p"` for a
   * single-paragraph Prose moment.
   */
  as?: React.ElementType;
  /**
   * Size variant.
   * - `md` (default) — 18px / 42rem
   * - `sm` — 16px / 36rem
   * - `lede` — first paragraph rendered at 18px / `--color-text-secondary`
   */
  size?: ProseSize;
  /**
   * Tone variant — affects link styling.
   * - `editorial` (default) — iris links, long underline. Marketing surfaces.
   * - `neutral` — text-primary links, subtle gray underline. Product surfaces.
   */
  tone?: ProseTone;
}

export function Prose({
  as: Tag = "div",
  size = "md",
  tone = "editorial",
  className = "",
  children,
  ...rest
}: ProseProps) {
  const sizeClass = size === "md" ? "" : `prose-${size}`;
  const toneClass = tone === "editorial" ? "" : `prose-${tone}`;
  return (
    <Tag className={cx("prose", sizeClass, toneClass, className)} {...rest}>
      {children}
    </Tag>
  );
}
